import { useEffect, useRef, useState } from "react";

interface DirectionsMenuProps {
  lat?: number;
  lng?: number;
  placeName?: string;
  placeId?: string;
  title?: string;
}

// "Get directions" button that opens a small menu of nav apps on
// touch devices (iOS in particular, where Universal Links can route
// https://maps.google.com links to Waze instead of Google Maps when
// both are installed). Desktop falls through to a single button that
// opens Google Maps web directly.
export function DirectionsMenu({
  lat,
  lng,
  placeName,
  placeId,
  title,
}: DirectionsMenuProps) {
  const hasCoords = typeof lat === "number" && typeof lng === "number";
  const dest = hasCoords ? `${lat},${lng}` : placeName || title || "";
  if (!dest) return null;

  const label = placeName || title || "destination";

  // Web / desktop URL — universal across platforms.
  const gmWebUrl = (() => {
    const params = new URLSearchParams({
      api: "1",
      destination: dest,
    });
    if (placeId) params.set("destination_place_id", placeId);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  })();

  // On desktop, the multi-app picker is just noise — one button, one
  // tab, done.
  const touch = isTouchDevice();
  if (!touch) {
    return (
      <a
        href={gmWebUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-deep text-xs font-semibold hover:bg-accent-light transition-colors"
      >
        <span aria-hidden="true">🧭</span>
        <span>Get directions</span>
      </a>
    );
  }

  return <TouchDirectionsMenu dest={dest} label={label} gmWebUrl={gmWebUrl} />;
}

function TouchDirectionsMenu({
  dest,
  label,
  gmWebUrl,
}: {
  dest: string;
  label: string;
  gmWebUrl: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onOutsideClick = (e: Event) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("touchstart", onOutsideClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("touchstart", onOutsideClick);
    };
  }, [open]);

  const encoded = encodeURIComponent(dest);

  const openGoogleMaps = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    // Try the Google Maps app first (unambiguous — Waze can't claim
    // this scheme). Fall back to the web URL if nothing intercepts.
    const appUrl = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
    tryApp(appUrl, gmWebUrl);
  };

  const openAppleMaps = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    // Apple Maps: maps:// always opens the Apple Maps app on iOS.
    // http://maps.apple.com is the universal URL that works on web
    // too (useful on Android as a backup viewer).
    window.location.href = `maps://?daddr=${encoded}&dirflg=d`;
    setTimeout(() => {
      if (!document.hidden) {
        window.open(`http://maps.apple.com/?daddr=${encoded}&dirflg=d`, "_blank");
      }
    }, 800);
  };

  const openWaze = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    // Waze supports a single destination via ll or via search.
    window.location.href = `waze://?ll=${encoded}&navigate=yes`;
    setTimeout(() => {
      if (!document.hidden) {
        window.open(
          `https://www.waze.com/ul?ll=${encoded}&navigate=yes`,
          "_blank"
        );
      }
    }, 800);
  };

  const ios = isIOS();

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-deep text-xs font-semibold hover:bg-accent-light transition-colors"
      >
        <span aria-hidden="true">🧭</span>
        <span>Get directions</span>
        <span aria-hidden="true" className="ml-0.5">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`Directions to ${label}`}
          className="absolute bottom-full left-0 mb-2 min-w-[180px] rounded-xl border border-white/15 bg-deep/95 shadow-2xl backdrop-blur-sm p-1 z-30"
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem icon="🗺️" label="Google Maps" onClick={openGoogleMaps} />
          {ios && (
            <MenuItem icon="" apple label="Apple Maps" onClick={openAppleMaps} />
          )}
          <MenuItem icon="🚗" label="Waze" onClick={openWaze} />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  apple,
  label,
  onClick,
}: {
  icon: string;
  apple?: boolean;
  label: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm text-ink hover:bg-white/10 transition-colors"
    >
      <span
        aria-hidden="true"
        className="w-5 text-center text-base leading-none"
      >
        {apple ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 inline-block" aria-hidden="true">
            <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.4 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.44zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.65-2.323-7.34 0-4.27 2.78-6.54 5.52-6.54 1.45 0 2.66.95 3.58.95.88 0 2.23-1.01 3.75-1.01.55 0 2.62.06 3.97 1.91-.35.22-2.44 1.44-2.44 3.86 0 2.43 2.12 3.53 2.43 3.68z" />
          </svg>
        ) : (
          icon
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || (navigator.maxTouchPoints ?? 0) > 0;
}

// Try to open an app-scheme URL; if the page is still visible after
// a short delay (meaning the app didn't intercept), open the fallback.
function tryApp(appUrl: string, fallbackUrl: string) {
  const started = Date.now();
  const onVisibility = () => {
    if (document.hidden) {
      // App took over — clean up and do nothing.
      document.removeEventListener("visibilitychange", onVisibility);
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.location.href = appUrl;
  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibility);
    // If the page never backgrounded, the app isn't installed — fall back.
    if (!document.hidden && Date.now() - started < 1500) {
      window.open(fallbackUrl, "_blank");
    }
  }, 800);
}
